import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import QueueService from './QueueService';
import createApp from '@shopify/app-bridge';
import { getSessionToken } from '@shopify/app-bridge-utils';
import { FaPlus, FaTrash, FaSave, FaCheck, FaTruck, FaStore, FaCalendarAlt, FaCog } from 'react-icons/fa';
import './OrderSettingsPage.css';

const DAYS_OF_WEEK = [
  { value: 0, label: 'Mon' }, { value: 1, label: 'Tue' }, { value: 2, label: 'Wed' },
  { value: 3, label: 'Thu' }, { value: 4, label: 'Fri' }, { value: 5, label: 'Sat' }, { value: 6, label: 'Sun' }
];

const DEFAULT_CONFIG = {
  is_enabled: true, timezone: 'Asia/Kolkata', weekly_rules: [], date_rules: [],
  scheduling_config: {
    is_enabled: true,
    methods: {
      local_delivery: { is_enabled: true, providers: [] },
      pickup: { is_enabled: true, locations: [] }
    }
  }
};

const PincodeInput = ({ pincodes, index, updateGroup }) => {
  const [val, setVal] = useState((pincodes || []).join(', '));
  
  useEffect(() => {
    // Sync if parent changes externally, but only if it differs from parsed current val
    const currentParsed = val.split(',').map(s => s.trim()).filter(Boolean).join(', ');
    const newParsed = (pincodes || []).join(', ');
    if (currentParsed !== newParsed) {
      setVal(newParsed);
    }
  }, [pincodes]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <input 
      className="osp-input" 
      value={val} 
      onChange={e => setVal(e.target.value)} 
      onBlur={e => {
        const newArr = e.target.value.split(',').map(s=>s.trim()).filter(Boolean);
        updateGroup(index, 'pincodes', newArr);
        setVal(newArr.join(', '));
      }} 
      placeholder="e.g. 122001, 122002" 
    />
  );
};

const OrderSettingsPage = ({ token: initialToken, business_id: initialBusinessId }) => {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('general'); 
  const [config, setConfig] = useState(JSON.parse(JSON.stringify(DEFAULT_CONFIG)));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState('');

  const searchParams = new URLSearchParams(location.search);
  const host = searchParams.get('host');
  const shop = searchParams.get('shop');
  const businessId = initialBusinessId || searchParams.get('business_id');

  useEffect(() => {
    const initFetch = async () => {
      // If we are in Shopify (host exists) but token hasn't been fetched by App.js yet, wait.
      if (host && !initialToken) {
        return;
      }

      try {
        if (businessId || shop) {
          const response = await QueueService.FetchOrderHours(businessId || '', initialToken, shop);
          const rulesData = response?.data?.order_rules || response?.order_rules;
          if (rulesData) {
            const data = JSON.parse(JSON.stringify(rulesData));
            if (!data.scheduling_config) data.scheduling_config = DEFAULT_CONFIG.scheduling_config;
            if (!data.scheduling_config.methods) data.scheduling_config.methods = DEFAULT_CONFIG.scheduling_config.methods;
            if (!data.scheduling_config.methods.local_delivery) data.scheduling_config.methods.local_delivery = DEFAULT_CONFIG.scheduling_config.methods.local_delivery;
            if (!data.scheduling_config.methods.pickup) data.scheduling_config.methods.pickup = DEFAULT_CONFIG.scheduling_config.methods.pickup;
            if (!data.scheduling_config.methods.local_delivery.providers) data.scheduling_config.methods.local_delivery.providers = [];
            if (!data.scheduling_config.methods.pickup.locations) data.scheduling_config.methods.pickup.locations = [];
            setConfig(data);
          }
        }
      } catch (err) { console.error(err); } finally { setLoading(false); }
    };
    initFetch();
  }, [host, shop, businessId, initialToken]);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  const handleSave = async () => {
    setSaving(true);
    try {
      await QueueService.UpdateOrderHours(businessId || '', config, initialToken, shop);
      showToast('Settings saved successfully');
    } catch (err) { console.error(err); } finally { setSaving(false); }
  };

  const updateField = (path, value) => {
    setConfig(prev => {
      const next = { ...prev };
      let current = next;
      for (let i = 0; i < path.length - 1; i++) {
        current[path[i]] = Array.isArray(current[path[i]]) ? [...current[path[i]]] : { ...current[path[i]] };
        current = current[path[i]];
      }
      current[path[path.length - 1]] = value;
      return next;
    });
  };

  // UI Helpers
  const renderDaysSelector = (selectedDays, onChange) => (
    <div className="osp-days">
      {DAYS_OF_WEEK.map(day => (
        <div key={day.value} 
             className={`osp-day-pill ${selectedDays.includes(day.value) ? 'active' : ''}`}
             onClick={() => {
               const newDays = selectedDays.includes(day.value) 
                  ? selectedDays.filter(d => d !== day.value) 
                  : [...selectedDays, day.value].sort();
               onChange(newDays);
             }}>
          {day.label}
        </div>
      ))}
    </div>
  );

  const renderSlot = (slot, index, updateSlot, removeSlot) => (
    <div key={index} className="osp-nested-item">
      <div className="osp-card-header" style={{ marginBottom: '8px' }}>
        <span className="osp-card-title">Slot: {slot.id || `slot_${index}`}</span>
        <button className="osp-btn osp-btn-danger-text" onClick={() => removeSlot(index)}><FaTrash size={10}/></button>
      </div>
      <div className="osp-form-group">
        <label className="osp-label">Label</label>
        <input className="osp-input" value={slot.label || ''} onChange={e => updateSlot(index, 'label', e.target.value)} placeholder="e.g. Lunch Rush" />
      </div>
      <div className="osp-form-group">
        <label className="osp-label">Active Days</label>
        {renderDaysSelector(slot.days_of_week || [], (val) => updateSlot(index, 'days_of_week', val))}
      </div>
      <div className="osp-row osp-form-group">
        <div className="osp-col">
          <label className="osp-label">Start Time</label>
          <input type="time" className="osp-input" value={slot.start || ''} onChange={e => updateSlot(index, 'start', e.target.value)} />
        </div>
        <div className="osp-col">
          <label className="osp-label">End Time</label>
          <input type="time" className="osp-input" value={slot.end || ''} onChange={e => updateSlot(index, 'end', e.target.value)} />
        </div>
      </div>
      <div className="osp-row">
        <div className="osp-col">
          <label className="osp-label">Capacity</label>
          <input type="number" className="osp-input" value={slot.capacity || 0} onChange={e => updateSlot(index, 'capacity', parseInt(e.target.value))} />
        </div>
        <div className="osp-col">
          <label className="osp-label">Lead Min Override</label>
          <input type="number" className="osp-input" value={slot.lead_minutes_override || ''} onChange={e => updateSlot(index, 'lead_minutes_override', parseInt(e.target.value) || null)} placeholder="Use global if empty" />
        </div>
      </div>
    </div>
  );

  const renderPincodeGroup = (group, index, updateGroup, removeGroup) => (
    <div key={index} className="osp-nested-item">
      <div className="osp-card-header" style={{ marginBottom: '8px' }}>
        <span className="osp-card-title">Zone: {group.name || `zone_${index}`}</span>
        <button className="osp-btn osp-btn-danger-text" onClick={() => removeGroup(index)}><FaTrash size={10}/></button>
      </div>
      <div className="osp-row osp-form-group">
        <div className="osp-col">
          <label className="osp-label">Zone Name</label>
          <input className="osp-input" value={group.name || ''} onChange={e => updateGroup(index, 'name', e.target.value)} placeholder="e.g. Zone 1" />
        </div>
        <div className="osp-col">
          <label className="osp-label">Shipping Fee</label>
          <input type="number" className="osp-input" value={group.shipping_fee || 0} onChange={e => updateGroup(index, 'shipping_fee', parseInt(e.target.value) || 0)} />
        </div>
      </div>
      <div className="osp-form-group">
        <label className="osp-label">Pincodes (comma separated)</label>
        <PincodeInput pincodes={group.pincodes} index={index} updateGroup={updateGroup} />
      </div>
    </div>
  );

  if (loading) return <div className="osp-wrapper"><p>Loading settings...</p></div>;

  return (
    <div className="osp-wrapper">
      {toast && <div className="osp-toast"><FaCheck /> {toast}</div>}
      
      <div className="osp-container">
        <div className="osp-header">
          <h1 className="osp-title">Order Settings</h1>
          <button className="osp-btn osp-btn-primary" onClick={handleSave} disabled={saving}>
            <FaSave /> {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
        
        <div className="osp-body">
          <div className="osp-sidebar">
            <div className={`osp-tab ${activeTab === 'general' ? 'active' : ''}`} onClick={() => setActiveTab('general')}>
              <FaCog /> General & Rules
            </div>
            <div className={`osp-tab ${activeTab === 'delivery' ? 'active' : ''}`} onClick={() => setActiveTab('delivery')}>
              <FaTruck /> Local Delivery
            </div>
            <div className={`osp-tab ${activeTab === 'pickup' ? 'active' : ''}`} onClick={() => setActiveTab('pickup')}>
              <FaStore /> Store Pickup
            </div>
          </div>

          <div className="osp-content">
            
            {/* GENERAL TAB */}
            {activeTab === 'general' && (
              <div>
                <div className="osp-section-title">Core Preferences</div>
                <div className="osp-card">
                  <div className="osp-row osp-items-center osp-mb-4">
                    <div className="osp-col">
                      <span className="osp-label" style={{marginBottom: 0}}>Accepting Orders</span>
                      <span className="osp-text-muted">Master toggle for all channels</span>
                    </div>
                    <label className="osp-switch-wrap">
                      <div className="osp-switch">
                        <input type="checkbox" checked={config.is_enabled} onChange={e => updateField(['is_enabled'], e.target.checked)} />
                        <span className="osp-slider"></span>
                      </div>
                    </label>
                  </div>
                  <div className="osp-form-group">
                    <label className="osp-label">Timezone</label>
                    <input className="osp-input" value={config.timezone} onChange={e => updateField(['timezone'], e.target.value)} />
                  </div>
                </div>

                <div className="osp-section-title">
                  Weekly Rules
                  <button className="osp-btn osp-btn-secondary" onClick={() => updateField(['weekly_rules'], [...config.weekly_rules, { days_of_week: [], time_range: {start:'09:00', end:'17:00'}, allowed: true }])}>
                    <FaPlus size={10}/> Add
                  </button>
                </div>
                {config.weekly_rules.map((rule, idx) => (
                  <div key={idx} className="osp-card">
                    <div className="osp-card-header">
                      <span className={`osp-badge ${rule.allowed ? 'osp-badge-green' : 'osp-badge-red'}`}>{rule.allowed ? 'ALLOWED' : 'BLOCKED'}</span>
                      <button className="osp-btn osp-btn-danger-text" onClick={() => updateField(['weekly_rules'], config.weekly_rules.filter((_, i) => i !== idx))}><FaTrash size={10}/></button>
                    </div>
                    <div className="osp-form-group">
                      {renderDaysSelector(rule.days_of_week || [], (val) => {
                        const newRules = [...config.weekly_rules]; newRules[idx].days_of_week = val; updateField(['weekly_rules'], newRules);
                      })}
                    </div>
                    <div className="osp-row osp-form-group">
                      <div className="osp-col">
                        <label className="osp-label">Start Time</label>
                        <input type="time" className="osp-input" value={rule.time_range?.start || ''} onChange={e => {
                          const r = [...config.weekly_rules]; if(!r[idx].time_range) r[idx].time_range={}; r[idx].time_range.start = e.target.value; updateField(['weekly_rules'], r);
                        }} />
                      </div>
                      <div className="osp-col">
                        <label className="osp-label">End Time</label>
                        <input type="time" className="osp-input" value={rule.time_range?.end || ''} onChange={e => {
                           const r = [...config.weekly_rules]; if(!r[idx].time_range) r[idx].time_range={}; r[idx].time_range.end = e.target.value; updateField(['weekly_rules'], r);
                        }} />
                      </div>
                    </div>
                    <div className="osp-row">
                      <div className="osp-col">
                        <label className="osp-label">Status</label>
                        <select className="osp-select" value={rule.allowed ? 'true' : 'false'} onChange={e => {
                          const r = [...config.weekly_rules]; r[idx].allowed = e.target.value==='true'; updateField(['weekly_rules'], r);
                        }}>
                          <option value="true">Allow</option><option value="false">Block</option>
                        </select>
                      </div>
                      <div className="osp-col">
                        <label className="osp-label">Note</label>
                        <input className="osp-input" value={rule.note || ''} onChange={e => {
                          const r = [...config.weekly_rules]; r[idx].note = e.target.value; updateField(['weekly_rules'], r);
                        }} />
                      </div>
                    </div>
                  </div>
                ))}

                <div className="osp-section-title" style={{marginTop: '24px'}}>
                  Date Overrides
                  <button className="osp-btn osp-btn-secondary" onClick={() => updateField(['date_rules'], [...config.date_rules, { date: '', time_range: null, allowed: false, note: '' }])}>
                    <FaPlus size={10}/> Add
                  </button>
                </div>
                {config.date_rules.map((rule, idx) => (
                  <div key={idx} className="osp-card">
                    <div className="osp-card-header">
                      <span className={`osp-badge ${rule.allowed ? 'osp-badge-green' : 'osp-badge-red'}`}>{rule.allowed ? 'ALLOWED' : 'BLOCKED'}</span>
                      <button className="osp-btn osp-btn-danger-text" onClick={() => updateField(['date_rules'], config.date_rules.filter((_, i) => i !== idx))}><FaTrash size={10}/></button>
                    </div>
                    <div className="osp-row osp-form-group">
                      <div className="osp-col">
                        <label className="osp-label">Date</label>
                        <input type="date" className="osp-input" value={rule.date || ''} onChange={e => {
                          const r = [...config.date_rules]; r[idx].date = e.target.value; updateField(['date_rules'], r);
                        }} />
                      </div>
                      <div className="osp-col">
                        <label className="osp-label">Status</label>
                        <select className="osp-select" value={rule.allowed ? 'true' : 'false'} onChange={e => {
                          const r = [...config.date_rules]; r[idx].allowed = e.target.value==='true'; updateField(['date_rules'], r);
                        }}>
                          <option value="true">Allow</option><option value="false">Block</option>
                        </select>
                      </div>
                    </div>
                    <div className="osp-row osp-form-group">
                      <div className="osp-col">
                        <label className="osp-label">Start Time (Optional)</label>
                        <input type="time" className="osp-input" value={rule.time_range?.start || ''} onChange={e => {
                          const r = [...config.date_rules]; if(!r[idx].time_range) r[idx].time_range={}; r[idx].time_range.start = e.target.value; updateField(['date_rules'], r);
                        }} />
                      </div>
                      <div className="osp-col">
                        <label className="osp-label">End Time (Optional)</label>
                        <input type="time" className="osp-input" value={rule.time_range?.end || ''} onChange={e => {
                           const r = [...config.date_rules]; if(!r[idx].time_range) r[idx].time_range={}; r[idx].time_range.end = e.target.value; updateField(['date_rules'], r);
                        }} />
                      </div>
                    </div>
                    <div className="osp-form-group" style={{marginBottom: 0}}>
                      <label className="osp-label">Note / Reason</label>
                      <input className="osp-input" value={rule.note || ''} onChange={e => {
                        const r = [...config.date_rules]; r[idx].note = e.target.value; updateField(['date_rules'], r);
                      }} placeholder="e.g. Christmas Day" />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* LOCAL DELIVERY TAB */}
            {activeTab === 'delivery' && (
              <div>
                <div className="osp-card osp-row osp-items-center osp-mb-4">
                  <div className="osp-col">
                    <span className="osp-label" style={{marginBottom:0}}>Enable Local Delivery</span>
                  </div>
                  <label className="osp-switch-wrap">
                    <div className="osp-switch">
                      <input type="checkbox" checked={config.scheduling_config.methods.local_delivery.is_enabled} onChange={e => updateField(['scheduling_config','methods','local_delivery','is_enabled'], e.target.checked)} />
                      <span className="osp-slider"></span>
                    </div>
                  </label>
                </div>

                <div className="osp-section-title">
                  Delivery Providers
                  <button className="osp-btn osp-btn-secondary" onClick={() => {
                    const prov = [...config.scheduling_config.methods.local_delivery.providers];
                    prov.push({ id: `prov_${Date.now()}`, label: 'New Provider', handling_fee: 0, global_lead_minutes: 60, pincode_groups: [], slots: [] });
                    updateField(['scheduling_config','methods','local_delivery','providers'], prov);
                  }}>
                    <FaPlus size={10}/> Add Provider
                  </button>
                </div>

                {config.scheduling_config.methods.local_delivery.providers.map((provider, pIdx) => (
                  <div key={pIdx} className="osp-card">
                     <div className="osp-card-header">
                      <span className="osp-card-title">{provider.label} ({provider.id})</span>
                      <button className="osp-btn osp-btn-danger-text" onClick={() => {
                        const prov = config.scheduling_config.methods.local_delivery.providers.filter((_, i) => i !== pIdx);
                        updateField(['scheduling_config','methods','local_delivery','providers'], prov);
                      }}><FaTrash size={10}/></button>
                    </div>

                    <div className="osp-row osp-form-group">
                      <div className="osp-col"><label className="osp-label">ID</label><input className="osp-input" value={provider.id} onChange={e => { const p = [...config.scheduling_config.methods.local_delivery.providers]; p[pIdx].id = e.target.value; updateField(['scheduling_config','methods','local_delivery','providers'], p); }} /></div>
                      <div className="osp-col"><label className="osp-label">Label</label><input className="osp-input" value={provider.label} onChange={e => { const p = [...config.scheduling_config.methods.local_delivery.providers]; p[pIdx].label = e.target.value; updateField(['scheduling_config','methods','local_delivery','providers'], p); }} /></div>
                    </div>
                    <div className="osp-row osp-form-group">
                      <div className="osp-col"><label className="osp-label">Handling Fee</label><input type="number" className="osp-input" value={provider.handling_fee} onChange={e => { const p = [...config.scheduling_config.methods.local_delivery.providers]; p[pIdx].handling_fee = parseInt(e.target.value); updateField(['scheduling_config','methods','local_delivery','providers'], p); }} /></div>
                      <div className="osp-col"><label className="osp-label">Global Lead Mins</label><input type="number" className="osp-input" value={provider.global_lead_minutes} onChange={e => { const p = [...config.scheduling_config.methods.local_delivery.providers]; p[pIdx].global_lead_minutes = parseInt(e.target.value); updateField(['scheduling_config','methods','local_delivery','providers'], p); }} /></div>
                    </div>

                    {/* Pincode Groups List */}
                    <div className="osp-nested-list">
                      <div className="osp-section-title" style={{fontSize:'12px'}}>
                        Serviceable Pincode Zones
                        <button className="osp-btn osp-btn-secondary" style={{padding:'4px 8px'}} onClick={() => {
                           const p = [...config.scheduling_config.methods.local_delivery.providers];
                           if (!p[pIdx].pincode_groups) p[pIdx].pincode_groups = [];
                           p[pIdx].pincode_groups.push({ name: 'New Zone', pincodes: [], shipping_fee: 0 });
                           updateField(['scheduling_config','methods','local_delivery','providers'], p);
                        }}><FaPlus size={8}/> Zone</button>
                      </div>
                      {provider.pincode_groups?.map((group, gIdx) => renderPincodeGroup(group, gIdx, 
                        (i, f, v) => { const p = [...config.scheduling_config.methods.local_delivery.providers]; p[pIdx].pincode_groups[i][f] = v; updateField(['scheduling_config','methods','local_delivery','providers'], p); },
                        (i) => { const p = [...config.scheduling_config.methods.local_delivery.providers]; p[pIdx].pincode_groups = p[pIdx].pincode_groups.filter((_, idx)=>idx!==i); updateField(['scheduling_config','methods','local_delivery','providers'], p); }
                      ))}
                    </div>

                    {/* Slots List */}
                    <div className="osp-nested-list">
                      <div className="osp-section-title" style={{fontSize:'12px'}}>
                        Dispatch Slots
                        <button className="osp-btn osp-btn-secondary" style={{padding:'4px 8px'}} onClick={() => {
                           const p = [...config.scheduling_config.methods.local_delivery.providers];
                           p[pIdx].slots.push({ id: `slot_${Date.now()}`, days_of_week: [], start: '10:00', end: '13:00', capacity: 10 });
                           updateField(['scheduling_config','methods','local_delivery','providers'], p);
                        }}><FaPlus size={8}/> Slot</button>
                      </div>
                      {provider.slots?.map((slot, sIdx) => renderSlot(slot, sIdx, 
                        (i, f, v) => { const p = [...config.scheduling_config.methods.local_delivery.providers]; p[pIdx].slots[i][f] = v; updateField(['scheduling_config','methods','local_delivery','providers'], p); },
                        (i) => { const p = [...config.scheduling_config.methods.local_delivery.providers]; p[pIdx].slots = p[pIdx].slots.filter((_, idx)=>idx!==i); updateField(['scheduling_config','methods','local_delivery','providers'], p); }
                      ))}
                    </div>

                  </div>
                ))}
              </div>
            )}

            {/* STORE PICKUP TAB */}
            {activeTab === 'pickup' && (
              <div>
                <div className="osp-card osp-row osp-items-center osp-mb-4">
                  <div className="osp-col">
                    <span className="osp-label" style={{marginBottom:0}}>Enable Store Pickup</span>
                  </div>
                  <label className="osp-switch-wrap">
                    <div className="osp-switch">
                      <input type="checkbox" checked={config.scheduling_config.methods.pickup.is_enabled} onChange={e => updateField(['scheduling_config','methods','pickup','is_enabled'], e.target.checked)} />
                      <span className="osp-slider"></span>
                    </div>
                  </label>
                </div>

                <div className="osp-section-title">
                  Pickup Locations
                  <button className="osp-btn osp-btn-secondary" onClick={() => {
                    const loc = [...config.scheduling_config.methods.pickup.locations];
                    loc.push({ id: `loc_${Date.now()}`, label: 'New Location', address: '', instructions: '', global_lead_minutes: 30, slots: [] });
                    updateField(['scheduling_config','methods','pickup','locations'], loc);
                  }}>
                    <FaPlus size={10}/> Add Location
                  </button>
                </div>

                {config.scheduling_config.methods.pickup.locations.map((loc, lIdx) => (
                  <div key={lIdx} className="osp-card">
                     <div className="osp-card-header">
                      <span className="osp-card-title">{loc.label} ({loc.id})</span>
                      <button className="osp-btn osp-btn-danger-text" onClick={() => {
                        const arr = config.scheduling_config.methods.pickup.locations.filter((_, i) => i !== lIdx);
                        updateField(['scheduling_config','methods','pickup','locations'], arr);
                      }}><FaTrash size={10}/></button>
                    </div>

                    <div className="osp-row osp-form-group">
                      <div className="osp-col"><label className="osp-label">ID</label><input className="osp-input" value={loc.id} onChange={e => { const a = [...config.scheduling_config.methods.pickup.locations]; a[lIdx].id = e.target.value; updateField(['scheduling_config','methods','pickup','locations'], a); }} /></div>
                      <div className="osp-col"><label className="osp-label">Label</label><input className="osp-input" value={loc.label} onChange={e => { const a = [...config.scheduling_config.methods.pickup.locations]; a[lIdx].label = e.target.value; updateField(['scheduling_config','methods','pickup','locations'], a); }} /></div>
                      <div className="osp-col"><label className="osp-label">Global Lead Mins</label><input type="number" className="osp-input" value={loc.global_lead_minutes} onChange={e => { const a = [...config.scheduling_config.methods.pickup.locations]; a[lIdx].global_lead_minutes = parseInt(e.target.value); updateField(['scheduling_config','methods','pickup','locations'], a); }} /></div>
                    </div>
                    
                    <div className="osp-form-group">
                      <label className="osp-label">Address</label>
                      <input className="osp-input" value={loc.address || ''} onChange={e => { const a = [...config.scheduling_config.methods.pickup.locations]; a[lIdx].address = e.target.value; updateField(['scheduling_config','methods','pickup','locations'], a); }} />
                    </div>

                     <div className="osp-form-group">
                      <label className="osp-label">Instructions</label>
                      <input className="osp-input" value={loc.instructions || ''} onChange={e => { const a = [...config.scheduling_config.methods.pickup.locations]; a[lIdx].instructions = e.target.value; updateField(['scheduling_config','methods','pickup','locations'], a); }} />
                    </div>

                    {/* Slots List */}
                    <div className="osp-nested-list">
                      <div className="osp-section-title" style={{fontSize:'12px'}}>
                        Pickup Slots
                        <button className="osp-btn osp-btn-secondary" style={{padding:'4px 8px'}} onClick={() => {
                           const a = [...config.scheduling_config.methods.pickup.locations];
                           a[lIdx].slots.push({ id: `slot_${Date.now()}`, days_of_week: [], start: '10:00', end: '18:00', capacity: 10 });
                           updateField(['scheduling_config','methods','pickup','locations'], a);
                        }}><FaPlus size={8}/> Slot</button>
                      </div>
                      {loc.slots?.map((slot, sIdx) => renderSlot(slot, sIdx, 
                        (i, f, v) => { const a = [...config.scheduling_config.methods.pickup.locations]; a[lIdx].slots[i][f] = v; updateField(['scheduling_config','methods','pickup','locations'], a); },
                        (i) => { const a = [...config.scheduling_config.methods.pickup.locations]; a[lIdx].slots = a[lIdx].slots.filter((_, idx)=>idx!==i); updateField(['scheduling_config','methods','pickup','locations'], a); }
                      ))}
                    </div>

                  </div>
                ))}
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderSettingsPage;
